import { useMemo } from 'react'

import type { PredictionContract, PredictionEvent } from '@/services/prediction/types'

/**
 * Compact FutureCard 最多展示的 outcome 行数，超出折叠成 "+N more outcomes"
 * （cdc-web `MAX_COMPACT_FUTURE_OUTCOMES`，Figma 4676-69631：所有变体恰好 2 行）。
 */
export const MAX_COMPACT_FUTURE_OUTCOMES = 2

export interface LeagueOutcomes {
  /** 要渲染的 outcome 行，至多 maxCount 条。 */
  displayContracts: PredictionContract[]
  /** 展示之外的 active 合约数 —— 驱动 "+N more" 页脚。 */
  remainingCount: number
}

/** cdc-web `getTopContract`：滤 active；不足时 ensureCount 回退全量补齐。 */
function getTopContracts(
  contracts: PredictionContract[],
  count: number,
  ensureCount = true,
): PredictionContract[] {
  let targets = contracts.filter(c => c.status === 'active')
  if (ensureCount && targets.length < count) targets = contracts
  return targets.slice(0, count)
}

/** cdc-web `isYesNoBinaryContracts`：恰好 2 条且含 'Yes'/'No'。 */
function isYesNoBinaryContracts(contracts: PredictionContract[]): boolean {
  if (contracts.length !== 2) return false
  const titles = contracts.map(c => c.contract_title)
  return titles.includes('Yes') && titles.includes('No')
}

/** cdc-web `isRecurringEvent`：duration > 0 的滚动事件。 */
const isRecurringEvent = (event: PredictionEvent) => event.duration != null && event.duration > 0

/**
 * cdc-web `sortRecurringContracts`：recurring 合约按概率距 50% 远近排序
 * （有 Yes 报价用 |50−chance|；仅 No 报价用 |50−no_chance|；都无报价按
 * 行权价 prediction_value 降序排尾）。
 */
function sortRecurringContracts(contracts: PredictionContract[]): PredictionContract[] {
  const getDiff = (c: PredictionContract): number | null => {
    if (c.yes) return Math.abs(50 - (Number.parseFloat(c.chance ?? '') || 0))
    if (c.no) return Math.abs(50 - (Number.parseFloat(c.no_chance ?? '') || 0))
    return null
  }
  const getStrikePrice = (c: PredictionContract) => Number.parseFloat(c.prediction_value ?? '') || 0
  return [...contracts].sort((a, b) => {
    const aDiff = getDiff(a)
    const bDiff = getDiff(b)
    if (aDiff !== null && bDiff !== null) return aDiff - bDiff
    if (aDiff !== null) return -1
    if (bDiff !== null) return 1
    return getStrikePrice(b) - getStrikePrice(a)
  })
}

/**
 * League/future outcome 切片的规范管线（cdc-web `useLeagueOutcomes` 裁剪版，
 * 只保留 League 分支——FutureCard 只接 league 事件）：
 *   1. `event.contracts` 顶层数组（league 的 sections 恒为空，BE 语义）；
 *   2. recurring（duration>0）按概率距 50% 排序；其余按 chance 降序（热度
 *      优先——BE 的 contracts 返回序是字母序，直接取前 N 会漏掉头部热门
 *      合约，与 stag 页不一致）；
 *   3. 取前 maxCount 条（active 过滤，不足时全量补齐）；
 *   4. Yes/No 二元事件把 Yes 排前。
 */
export function useLeagueOutcomes(event: PredictionEvent, maxCount: number): LeagueOutcomes {
  const contracts = useMemo(() => event.contracts ?? [], [event.contracts])

  return useMemo(() => {
    const sorted = isRecurringEvent(event)
      ? sortRecurringContracts(contracts)
      : [...contracts].sort((a, b) => Number(b.chance ?? 0) - Number(a.chance ?? 0))
    const top = getTopContracts(sorted, maxCount)
    const displayContracts = isYesNoBinaryContracts(top)
      ? [...top].sort((a, b) => {
          if (a.contract_title === 'Yes' && b.contract_title !== 'Yes') return -1
          if (a.contract_title !== 'Yes' && b.contract_title === 'Yes') return 1
          return 0
        })
      : top

    const totalActive = contracts.filter(c => c.status === 'active').length
    const remainingCount = Math.max(0, totalActive - displayContracts.length)

    return { displayContracts, remainingCount }
  }, [contracts, event, maxCount])
}
